import { createSlice, PayloadAction } from "@reduxjs/toolkit"
import { RootState } from "../store"
import { withCampusLogoFallbacks } from "../../../../utils/campusLogos"

export interface Branch {
  _id: string
  name: string
  campusNo?: string
  code?: string
  schoolName?: string
  schoolCode?: string
  logoKey?: string
  logoUrl?: string | null
  blackLogoKey?: string
  blackLogoUrl?: string | null
  strokeLogoKey?: string
  strokeLogoUrl?: string | null
  address?: string
  contactNumber?: string
  // Which academic Systems (Matric/O-Level/AKU/College) this campus offers —
  // drives which Systems/Classes/Groups show up once a campus is selected.
  systems?: { _id: string; name: string; code?: string; style?: "local" | "cambridge" }[]
  // Default attendance status for this campus (student/staff have their own
  // setting) — e.g. drives whether the barcode scanner marks Present or
  // Absent on the Student Attendance screen.
  studentAttendanceDefault?: "present" | "absent"
  staffAttendanceDefault?: "present" | "absent"
}

interface BranchesState {
  branches: Branch[]
  currentBranch: Branch | null
}

// Same key the apiClient reads to attach the x-branch header. null selection
// (= "All Branches") is persisted as "all".
const CURRENT_BRANCH_KEY = "currentBranchId"
const IMPERSONATION_TOKEN_KEY = "impersonationToken"
const activeStorage = () => {
  try {
    return sessionStorage.getItem(IMPERSONATION_TOKEN_KEY)
      ? sessionStorage
      : localStorage
  } catch {
    return localStorage
  }
}

const persistBranch = (b: Branch | null) => {
  try {
    activeStorage().setItem(CURRENT_BRANCH_KEY, b ? b._id : "all")
  } catch {
    /* ignore */
  }
}

const initialState: BranchesState = {
  branches: [],
  currentBranch: null,
}

const branchesSlice = createSlice({
  name: "branches",
  initialState,
  reducers: {
    setBranches: (state, action: PayloadAction<Branch[]>) => {
      // Always present campuses in campus-number order (1,2,3…) regardless of
      // the order the API returns them in.
      state.branches = action.payload.map(withCampusLogoFallbacks).sort(
        (a, b) => Number(a.campusNo ?? 0) - Number(b.campusNo ?? 0)
      )
      // Restore the previously-selected campus after a reload (for the UI).
      try {
        const saved = activeStorage().getItem(CURRENT_BRANCH_KEY)
        if (saved && saved !== "all") {
          const match = state.branches.find((x) => x._id === saved)
          if (match) state.currentBranch = match
        }
      } catch {
        /* ignore */
      }
      // Single-campus model (no "All Campuses"): always keep exactly one campus
      // selected. Default to the first campus if none/invalid was restored.
      if (!state.currentBranch && state.branches.length) {
        state.currentBranch = state.branches[0]
        persistBranch(state.branches[0])
      }
    },
    setCurrentBranch: (state, action: PayloadAction<Branch | null>) => {
      state.currentBranch = action.payload ? withCampusLogoFallbacks(action.payload) : null
      persistBranch(state.currentBranch)
    },
  },
})

export const { setBranches, setCurrentBranch } = branchesSlice.actions

export const selectBranches = (state: RootState) => state.branches.branches

export const selectCurrentBranch = (state: RootState) => {
  if (state.branches.currentBranch) return state.branches.currentBranch
  // Single-campus model: default to the first campus (never "All").
  if (state.branches.branches.length) return state.branches.branches[0]
  return null
}

export default branchesSlice.reducer
